export type SdkBrandLogoCopy = {
	language: string
	name: string
	subtitle: string
}

export type SdkBrandLogoTheme = {
	foreground: string
	muted: string
	primary: string
	secondary: string
}

export type SdkFallbackArtworkTheme = {
	primary: string
	primary10: string
	primary14: string
	primary20: string
	primary28: string
	primary36: string
	secondary: string
}

type SdkBrandLogoMarkOptions = {
	animated?: boolean
	inverted?: boolean
	size: number
	theme: SdkBrandLogoTheme
	variant?: "brand" | "mark"
}

type SdkBrandLogoOptions = {
	animated?: boolean
	brand: SdkBrandLogoCopy
	brandWidth?: number
	copyWidth?: number
	gap?: number
	markMarginRight?: number
	markSize: number
	nameSize: number
	subtitleMarginTop?: number
	subtitleSize: number
	subtitleWeight?: string
	theme: SdkBrandLogoTheme
	variant?: "brand" | "mark"
}

function ensureSdkBrandLogoStyles() {
	if (document.getElementById?.("youidian-sdk-brand-logo-style")) {
		return
	}

	const style = document.createElement("style")
	style.id = "youidian-sdk-brand-logo-style"
	style.textContent = `
		.youidian-sdk-brand-logo__ring,
		.youidian-sdk-brand-logo__dot,
		.youidian-sdk-brand-logo__copy,
		.youidian-sdk-brand-logo__tagline {
			transform-box: fill-box;
			transform-origin: center;
			will-change: opacity, transform;
		}

		.youidian-sdk-brand-logo[data-animated="true"] .youidian-sdk-brand-logo__ring {
			animation: youidian-sdk-brand-logo-ring 1400ms cubic-bezier(0.4, 0, 0.2, 1) infinite;
		}

		.youidian-sdk-brand-logo[data-animated="true"] .youidian-sdk-brand-logo__dot {
			animation: youidian-sdk-brand-logo-dot 1400ms cubic-bezier(0.4, 0, 0.2, 1) infinite;
		}

		.youidian-sdk-brand-logo[data-animated="true"] .youidian-sdk-brand-logo__copy {
			animation: youidian-sdk-brand-logo-copy 1600ms cubic-bezier(0.4, 0, 0.2, 1) infinite;
		}

		.youidian-sdk-brand-logo[data-animated="true"] .youidian-sdk-brand-logo__tagline {
			animation: youidian-sdk-brand-logo-tagline 1600ms cubic-bezier(0.4, 0, 0.2, 1) infinite;
		}

		@keyframes youidian-sdk-brand-logo-ring {
			0%,
			100% {
				opacity: 0.72;
				transform: rotate(0deg) scale(0.98);
			}
			50% {
				opacity: 1;
				transform: rotate(8deg) scale(1.02);
			}
		}

		@keyframes youidian-sdk-brand-logo-dot {
			0%,
			100% {
				opacity: 0.58;
				transform: scale(0.9);
			}
			50% {
				opacity: 1;
				transform: scale(1.08);
			}
		}

		@keyframes youidian-sdk-brand-logo-copy {
			0%,
			100% {
				opacity: 0.86;
				transform: translateY(0);
			}
			50% {
				opacity: 1;
				transform: translateY(-1px);
			}
		}

		@keyframes youidian-sdk-brand-logo-tagline {
			0%,
			100% {
				opacity: 0.64;
			}
			50% {
				opacity: 0.92;
			}
		}

		@media (prefers-reduced-motion: reduce) {
			.youidian-sdk-brand-logo__ring,
			.youidian-sdk-brand-logo__dot,
			.youidian-sdk-brand-logo__copy,
			.youidian-sdk-brand-logo__tagline {
				animation: none;
				opacity: 1;
				transform: none;
			}
		}
	`
	document.head?.appendChild(style)
}

export function createSdkBrandLogoMark({
	animated,
	inverted,
	size,
	theme,
	variant = "brand",
}: SdkBrandLogoMarkOptions) {
	ensureSdkBrandLogoStyles()

	const mark = document.createElementNS("http://www.w3.org/2000/svg", "svg")
	mark.setAttribute("viewBox", "0 0 512 512")
	mark.setAttribute("fill", "none")
	mark.setAttribute("aria-hidden", "true")
	Object.assign(mark.style, {
		width: `${size}px`,
		height: `${size}px`,
		color: inverted ? "#ffffff" : theme.primary,
		display: "block",
		flex: "0 0 auto",
		overflow: "visible",
	})

	const logoGroup = document.createElementNS("http://www.w3.org/2000/svg", "g")
	logoGroup.setAttribute(
		"transform",
		variant === "mark"
			? "matrix(0.74 0 0 0.74 -124 -114)"
			: "matrix(0.58 0 0 0.58 -40 -31)",
	)

	const ring = document.createElementNS("http://www.w3.org/2000/svg", "path")
	ring.setAttribute("class", animated ? "youidian-sdk-brand-logo__ring" : "")
	ring.setAttribute("fill", inverted ? "currentColor" : theme.primary)
	ring.setAttribute(
		"d",
		"M704.298 679.54A264 264 0 1 1 704.298 309.46A49 49 0 0 1 634.4 378.149A166 166 0 1 0 634.4 610.851A49 49 0 0 1 704.298 679.54Z",
	)

	const dot = document.createElementNS("http://www.w3.org/2000/svg", "circle")
	dot.setAttribute("class", animated ? "youidian-sdk-brand-logo__dot" : "")
	dot.setAttribute("fill", inverted ? "currentColor" : theme.secondary)
	dot.setAttribute("cx", "714.5")
	dot.setAttribute("cy", "490.5")
	dot.setAttribute("r", "68")
	dot.setAttribute("opacity", inverted ? "0.78" : "1")

	logoGroup.appendChild(ring)
	logoGroup.appendChild(dot)
	mark.appendChild(logoGroup)
	return mark
}

export function createSdkBrandLogo({
	animated,
	brand,
	brandWidth,
	copyWidth,
	gap = 10,
	markMarginRight,
	markSize,
	nameSize,
	subtitleMarginTop = 6,
	subtitleSize,
	subtitleWeight = "800",
	theme,
	variant = "brand",
}: SdkBrandLogoOptions) {
	ensureSdkBrandLogoStyles()

	const wrapper = document.createElement("div")
	wrapper.className = "youidian-sdk-brand-logo"
	wrapper.setAttribute("data-animated", animated ? "true" : "false")
	Object.assign(wrapper.style, {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		gap: `${gap}px`,
		width: brandWidth ? `${brandWidth}px` : undefined,
	})

	const copy = document.createElement("div")
	copy.className = "youidian-sdk-brand-logo__copy"
	copy.setAttribute("data-brand-locale", brand.language)
	Object.assign(copy.style, {
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-start",
		justifyContent: "center",
		lineHeight: "1",
		width: copyWidth ? `${copyWidth}px` : undefined,
	})

	const name = document.createElement("div")
	name.className = "youidian-sdk-brand-logo__name"
	name.textContent = brand.name
	Object.assign(name.style, {
		color: theme.foreground,
		fontFamily:
			brand.language === "zh"
				? '"Noto Sans TC", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
				: '"Unbounded", "Space Grotesk", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
		fontSize: `${nameSize}px`,
		fontWeight: brand.language === "zh" ? "900" : "800",
		letterSpacing: brand.language === "zh" ? "0.08em" : "0.01em",
		lineHeight: "1",
		whiteSpace: "nowrap",
	})

	const subtitle = document.createElement("div")
	subtitle.className = "youidian-sdk-brand-logo__tagline"
	subtitle.textContent = brand.subtitle
	Object.assign(subtitle.style, {
		color: theme.muted,
		fontFamily:
			brand.language === "zh"
				? '"Noto Sans TC", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
				: '"Space Grotesk", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
		fontSize: `${subtitleSize}px`,
		fontWeight: subtitleWeight,
		letterSpacing: brand.language === "zh" ? "0.24em" : "0.07em",
		lineHeight: "1",
		marginTop: `${subtitleMarginTop}px`,
		textTransform: brand.language === "zh" ? "none" : "uppercase",
		whiteSpace: "nowrap",
	})

	copy.appendChild(name)
	copy.appendChild(subtitle)
	const mark = createSdkBrandLogoMark({
		animated,
		size: markSize,
		theme,
		variant,
	})
	if (typeof markMarginRight === "number") {
		mark.style.marginRight = `${markMarginRight}px`
	}
	wrapper.appendChild(mark)
	wrapper.appendChild(copy)
	return wrapper
}

export function createSdkFallbackArtwork(theme: SdkFallbackArtworkTheme) {
	const artwork = document.createElement("div")
	Object.assign(artwork.style, {
		height: "164px",
		margin: "0 auto 34px",
		position: "relative",
		width: "184px",
	})

	const wash = document.createElement("div")
	Object.assign(wash.style, {
		position: "absolute",
		left: "16px",
		top: "20px",
		width: "152px",
		height: "126px",
		borderRadius: "48% 52% 43% 57% / 54% 42% 58% 46%",
		background: `radial-gradient(circle at 48% 42%, ${theme.primary20}, ${theme.primary10} 52%, transparent 76%)`,
		filter: "blur(1px)",
		transform: "rotate(-10deg)",
	})

	const blob = document.createElement("div")
	Object.assign(blob.style, {
		position: "absolute",
		left: "26px",
		top: "28px",
		width: "132px",
		height: "116px",
		borderRadius: "42% 58% 53% 47% / 45% 39% 61% 55%",
		background: `linear-gradient(150deg, ${theme.primary14}, ${theme.primary10})`,
		boxShadow: `0 30px 64px ${theme.primary14}`,
		transform: "rotate(8deg)",
	})

	const tile = document.createElement("div")
	Object.assign(tile.style, {
		position: "absolute",
		left: "50%",
		top: "50%",
		transform: "translate(-50%, -50%) rotate(-4deg)",
		width: "112px",
		height: "106px",
		borderRadius: "38% 62% 50% 50% / 44% 43% 57% 56%",
		background: `linear-gradient(145deg, ${theme.primary}, ${theme.secondary})`,
		border: `1px solid ${theme.primary36}`,
		color: "#ffffff",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		boxShadow: `0 26px 54px ${theme.primary28}`,
	})

	const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg")
	icon.setAttribute("viewBox", "0 0 24 24")
	icon.setAttribute("fill", "none")
	icon.setAttribute("aria-hidden", "true")
	Object.assign(icon.style, {
		display: "block",
		flex: "0 0 auto",
		height: "52px",
		transform: "rotate(4deg)",
		width: "52px",
	})

	for (const value of [
		"M12 7V12L15 14",
		"M21 12A9 9 0 1 1 3 12A9 9 0 0 1 21 12",
	]) {
		const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
		path.setAttribute("d", value)
		path.setAttribute("stroke", "currentColor")
		path.setAttribute("stroke-width", "2")
		path.setAttribute("stroke-linecap", "round")
		path.setAttribute("stroke-linejoin", "round")
		icon.appendChild(path)
	}

	tile.appendChild(icon)
	artwork.appendChild(wash)
	artwork.appendChild(blob)
	artwork.appendChild(tile)
	return artwork
}
